import { useEffect, useState } from 'react';
import { Alert, Note, Panel } from '../../ui';
import type { ProductImage } from '../../api/catalog';

/**
 * A product's photographs — a rail of thumbnails against one large frame, which
 * is how a customer will see them and therefore how an administrator should
 * check them.
 *
 * Grouped by METAL COLOUR because that is what the set belongs to: a customer
 * choosing rose gold sees the rose photographs whatever carat they pick. A metal
 * with no colour axis — anything not gold — has one unkeyed set, and then the
 * colour tabs are pointless and are not drawn.
 */
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * Metal colour -> what it actually looks like, not just its name. The same
 * three-value hex map the customer catalog's own facet swatches use
 * (`Shop.tsx`'s `COLOUR_HUE`, itself taken from the prototype's reference
 * HTML) — reused rather than re-derived, so "Yellow" reads as the same
 * yellow everywhere in the app. A colour this map doesn't name falls back to
 * a neutral swatch rather than rendering nothing.
 */
const COLOUR_HUE: Record<string, string> = { Yellow: '#D9B45B', White: '#DBDBDF', Rose: '#DBA79B' };
const SWATCH_FALLBACK = '#F2EFEC';

export function ImagePanel(
  { images, offeredColours = [], exists = true, readOnly, pending, onRemove, onAdd, onSetPrimary, progress }: {
    images: ProductImage[];
    /**
     * Whether the product has been saved yet.
     *
     * A photograph is attached to a product id, so there is nothing to attach it
     * to before the first save. The drop zone used to be offered anyway and the
     * upload failed with `Cannot read properties of undefined` — a TypeError
     * shown to an author as though it were an answer. Now the panel says what to
     * do instead.
     */
    exists?: boolean;
    /**
     * The metal colours this design is OFFERED in — `{ code, label }`, from the
     * product's own axis selection.
     *
     * Without this the tabs were derived from the images themselves, which is
     * circular: a product with no photographs offered no colour to upload into,
     * so the first file of every colour landed with a null colour meaning
     * "applies to all of them". Nine client photographs, three per colour, all
     * arrived as one undifferentiated pile and a white-gold ring showed under
     * Rose.
     *
     * Empty for a metal with no colour axis — platinum and silver — and then
     * there is genuinely one unkeyed set and no tabs, which is the old
     * behaviour and still correct.
     */
    offeredColours?: ReadonlyArray<{ code: string; label: string }>;
    readOnly: boolean;
    pending: boolean;
    onRemove: (imageId: string) => void;
    /**
     * Mark the one photograph that represents this design.
     *
     * Sort order could not say this: it orders images within a colour, so the
     * catalog card led with whichever colour ranked first — for Gaia Grid
     * Necklace, a side-on shot of the pendant edge. One per product across
     * every colour, which is why the badge and the button read "primary" and
     * not "first".
     */
    onSetPrimary?: (imageId: string) => void;
    onAdd: (files: File[], colour: string | null) => void;
    /** How far through a batch, so the panel can say so rather than just disabling. */
    progress?: { done: number; total: number } | null;
  },
) {
  /**
   * Every colour that can hold a set: the ones offered, plus any a stored image
   * already carries.
   *
   * The second half matters — a colour switched off after its photographs were
   * taken must still show them, or they become unreachable and undeletable
   * (CR-12: switching a value off does not delete what already carries it).
   */
  const offered = offeredColours.map((c) => c.code);
  const fromImages = [...new Set(images.map((i) => i.metalColour))]
    .filter((c): c is string => c !== null && !offered.includes(c));
  const colours: Array<string | null> = [...offered, ...fromImages];
  if (colours.length === 0 || images.some((i) => i.metalColour === null)) colours.push(null);

  /**
   * The selection is DERIVED rather than synced.
   *
   * Two things can invalidate it: a colour losing its last image, and the images
   * arriving after the first render. An effect that corrected state afterwards
   * would render one frame pointing at nothing first, and would need `colours` in
   * its dependencies — an array rebuilt every render, so it would loop. Choosing
   * during render has neither problem.
   */
  const [wanted, setWanted] = useState<string | null | undefined>(undefined);
  const colour = wanted !== undefined && colours.includes(wanted) ? wanted : (colours[0] ?? null);

  const set = images.filter((i) => i.metalColour === colour);
  const [wantedShot, setWantedShot] = useState(0);
  const shot = Math.min(wantedShot, Math.max(set.length - 1, 0));
  const current = set[shot];

  const setColour = (c: string | null) => { setWanted(c); setWantedShot(0); };
  const setShot = setWantedShot;

  const [dragging, setDragging] = useState(false);

  /**
   * Accept a batch from any of the three ways they arrive — picker, drop, paste.
   *
   * Filtered here rather than relying on the picker's `accept`, because a drop
   * and a paste do not honour it: dragging a PDF onto the panel would otherwise
   * reach the server to be refused there, which is a slow way to learn.
   */
  const ACCEPT = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'video/webm'];
  /**
   * `busy` is said out loud, not swallowed.
   *
   * This used to be `if (readOnly || pending) return` — so a set dropped while
   * the previous one was still uploading vanished without a word. Found by
   * loading the client's nine photographs three colours at a time: yellow and
   * rose arrived, white did not, and nothing on screen said why. Uploads are
   * sequential because `sort_order` is assigned server-side from what is
   * already there, so "wait" is the honest answer — but it has to be an answer.
   */
  const [busy, setBusy] = useState<string | null>(null);
  function take(files: File[]) {
    if (readOnly) return;
    if (pending) {
      setBusy(`Still uploading. ${plural(files.length, 'file was', 'files were')} not added — drop again when this batch finishes.`);
      return;
    }
    setBusy(null);
    const usable = files.filter((f) => ACCEPT.includes(f.type));
    const rejected = files.length - usable.length;
    if (rejected > 0) {
      setBusy(`${plural(rejected, 'file is', 'files are')} not an image or a video Miraki accepts, and ${rejected === 1 ? 'was' : 'were'} not added.`);
    }
    if (usable.length) onAdd(usable, colour);
  }

  /**
   * Paste, listened for on the document rather than the panel.
   *
   * A person pressing Cmd+V expects it to work without having clicked the panel
   * first. The cost is having to ignore pastes aimed at a field — otherwise
   * pasting a product name into the name box would try to upload it.
   */
  useEffect(() => {
    if (readOnly) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      const files = [...(e.clipboardData?.files ?? [])];
      if (files.length) { e.preventDefault(); take(files); }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  });

  const label = (c: string | null) =>
    offeredColours.find((o) => o.code === c)?.label
    ?? images.find((i) => i.metalColour === c)?.metalColourLabel
    ?? 'No colour';

  return (
    <Panel
      title={(() => {
        const v = images.filter((i) => i.kind === 'VIDEO').length;
        const pics = images.length - v;
        const bits = [
          `${pics} image${pics === 1 ? '' : 's'}`,
          ...(v ? [`${v} video${v === 1 ? '' : 's'}`] : []),
        ];
        return `Media — ${bits.join(', ')} across ${colours.length || 0} colour set${colours.length === 1 ? '' : 's'}`;
      })()}
      pad
    >
      {/*
        * The colour tabs render even with NO images, and that is the whole point
        * of taking `offeredColours`: they are what an author picks BEFORE
        * uploading, so a yellow-gold photograph is filed as yellow gold. Derived
        * from the images instead, an empty product offered no colour to choose
        * and every first upload landed as "applies to all of them".
        */}
      <>
          {colours.length > 1 ? (
            <div className="mk-ctabs">
              {colours.map((c) => (
                <button
                  key={c ?? 'none'}
                  type="button"
                  className={`mk-ctab${c === colour ? ' on' : ''}`}
                  aria-pressed={c === colour}
                  // The swatch carries the colour visually and nothing else names it
                  // on screen, so the accessible name (and a hover title, for a
                  // sighted person who wants the word) has to come from here.
                  aria-label={label(c)}
                  title={label(c)}
                  onClick={() => setColour(c)}
                >
                  <span
                    className="mk-swatch"
                    style={{ background: COLOUR_HUE[label(c)] ?? SWATCH_FALLBACK }}
                    aria-hidden="true"
                  />
                  <span className="mk-ctab-n">{images.filter((i) => i.metalColour === c).length}</span>
                </button>
              ))}
            </div>
          ) : null}

          {images.length === 0 ? (
            <Note>
              No media for this product{colours.length > 1 ? ` in ${label(colour)}` : ''}. A colour
              with none falls back to the first colour that has them, so a design can go live with
              one set and gain the others later.
            </Note>
          ) : set.length === 0 ? (
            <Note>
              Nothing in {label(colour)} yet — anything dropped below is filed here. Other colours
              have their own sets, and a customer choosing this one sees the first colour that has
              photographs until it does.
            </Note>
          ) : (
          <div className="mk-media">
            <div className="mk-media-rail">
              {set.map((img, i) => (
                <button
                  key={img.id}
                  type="button"
                  className={`mk-thumb${i === shot ? ' on' : ''}`}
                  aria-pressed={i === shot}
                  aria-label={img.caption ?? `Image ${i + 1}`}
                  onClick={() => setShot(i)}
                  onMouseEnter={() => setShot(i)}
                >
                  {img.kind === 'VIDEO' ? (
                    <>
                      {/* No poster is generated — that needs ffmpeg, which is the
                          derivation step WS-3 owns. `preload="metadata"` makes the
                          browser draw a frame without one. */}
                      <video src={img.url} preload="metadata" muted />
                      <span className="mk-thumb-play" aria-hidden="true" />
                    </>
                  ) : (
                    <img src={img.url} alt="" loading="lazy" />
                  )}
                  {img.isPrimary ? <span className="mk-thumb-star" title="Primary image">★</span> : null}
                </button>
              ))}
            </div>

            <figure className="mk-media-main">
              {!current ? null : current.kind === 'VIDEO' ? (
                // `controls` and nothing else: no autoplay, no loop, no muted
                // trickery. An administrator checking a clip wants to press play,
                // and a video that starts on its own in a catalog screen is a
                // surprise rather than a feature.
                <video src={current.url} controls preload="metadata" />
              ) : (
                <img src={current.url} alt={current.caption ?? ''} />
              )}
              {readOnly || !current || !onSetPrimary || current.kind === 'VIDEO' ? null : (
                /* Marking, never un-marking: exactly one image is primary, so
                   there is no "off" state to offer — choosing another is how
                   you change it. The button says so by disabling on the one
                   that already holds it. */
                <button
                  type="button"
                  className={`mk-media-primary${current.isPrimary ? ' on' : ''}`}
                  disabled={pending || current.isPrimary}
                  aria-pressed={current.isPrimary}
                  aria-label={current.isPrimary
                    ? 'This is the primary image'
                    : `Make ${current.caption ?? 'this image'} the primary image`}
                  title={current.isPrimary
                    ? 'Primary image — shown first everywhere'
                    : 'Make this the primary image'}
                  onClick={() => onSetPrimary(current.id)}
                >
                  <span aria-hidden="true">★</span>
                  {current.isPrimary ? ' Primary' : ' Make primary'}
                </button>
              )}
              {readOnly || !current ? null : (
                // Top right, over the image, the way a person expects to remove
                // the thing they are looking at. Its own element rather than the
                // shared Button, which takes no class and would need one only for
                // this position.
                <button
                  type="button"
                  className="mk-media-remove"
                  disabled={pending}
                  // The accessible name carries the meaning; the glyph is only
                  // the affordance. A screen reader announces "Remove Front",
                  // not "times".
                  aria-label={`Remove ${current.caption ?? (current.kind === 'VIDEO' ? 'this video' : 'this image')}`}
                  title={`Remove ${current.caption ?? (current.kind === 'VIDEO' ? 'this video' : 'this image')}`}
                  onClick={() => onRemove(current.id)}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              )}
              <figcaption>
                {current?.caption || `${current?.kind === 'VIDEO' ? 'Video' : 'Image'} ${shot + 1} of ${set.length}`}
                {colours.length > 1 ? ` · ${label(colour)}` : null}
              </figcaption>
            </figure>
          </div>
          )}
      </>
      {!exists ? (
        <Note>
          Save the product first, then add photographs or videos. A file belongs to a product and a metal
          colour, so both have to exist before there is anywhere to put it.
        </Note>
      ) : readOnly ? null : (
        <div
          className={`mk-drop${dragging ? ' on' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          // `relatedTarget` outside the zone, so moving over a CHILD of the drop
          // area does not read as leaving it and flicker the highlight.
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            take([...e.dataTransfer.files]);
          }}
        >
          <div className="mk-drop-say">
            <b>Drop files here</b>, paste from the clipboard, or{' '}
            <label className="mk-linkish">
              choose them
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm"
                multiple
                hidden
                disabled={pending}
                onChange={(e) => {
                  const list = [...(e.target.files ?? [])];
                  // Reset first: choosing the SAME file twice fires no change
                  // event otherwise, which reads as a dead control.
                  e.target.value = '';
                  take(list);
                }}
              />
            </label>
            .
            <div className="mk-hint">
              {progress
                ? `Uploading ${progress.done} of ${progress.total}… `
                : colours.length > 1 ? `Added to ${label(colour)}. ` : ''}
              JPEG, PNG or WebP up to 25MB, MP4 or WebM up to 50MB. Several at once is fine.
            </div>
            {busy ? <Alert tone="us">{busy}</Alert> : null}
          </div>
        </div>
      )}
    </Panel>
  );
}
